/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import { PayPalApiActionTypes } from 'app/paypal/components/PayPalView/state/actions';

/**
 * @typedef {Object} Account
 * @property {String} id - the account's id
 * @property {Boolean} charges_enabled - Whether charges are enabled
 * @property {Boolean} details_submitted - Whether the account has finished on-boarding
 * @property {Boolean} payouts_enabled - Whether payouts are enabled
 */

/**
 * @typedef {Object} AccountLinks
 * @property {String} url - The URL that will take the user to the PayPal dashboard
 */

/**
 * @typedef {Object} PayPalApiState
 * @property {Account|AccountLinks|null} [data=null] - The Account or AccountLinks fetched from the config server
 * @property {string|null} [fetchError=null] - the error message if a fetch error occurred
 * @property {boolean} [isFetching=false] - whether data is being fetched
 * @property {boolean} [isFetchError=false] - whether a fetch error has occurred
 * @property {boolean} [isFetchQueued=false] - whether the fetch lifecycle is queued to execute
 * @property {number|null} [lastUpdated=null] - the last time the fetched data was updated
 */

/**
 * @type {PayPalApiState}
 */
const defaultState = {
  data: null,
  fetchError: null,
  isFetching: false,
  isFetchError: false,
  lastUpdated: null
};

/**
 * Reducer used for managing the state of interactions with the PayPal API.
 *
 * @param {PayPalApiState} [state=defaultState] - The current state
 * @param {{ type: string, [payload]: * }} action - the action to reduce
 * @return {PayPalApiState}
 */
function defaultReducer(state = defaultState, action) {
  switch (action.type) {
    case PayPalApiActionTypes.START_PAYPAL: {
      return {
        ...state,
        isFetching: true,
        isFetchQueued: false
      };
    }
    case PayPalApiActionTypes.SUCCESS_PAYPAL: {
      return {
        ...state,
        data: action.payload.data,
        fetchError: null,
        isFetching: false,
        isFetchError: false,
        lastUpdated: action.payload.receivedAt
      };
    }
    case PayPalApiActionTypes.ERROR_PAYPAL: {
      return {
        ...state,
        fetchError: action.payload,
        isFetching: false,
        isFetchError: true
      };
    }
    default:
      return state;
  }
}

export default defaultReducer;
export { defaultState };
